import type { Balance, Credit, Goal, MemberOffer, Recommendation, Redemption } from "./types";

export function money(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function calculateBalance(credits: Credit[], redemptions: Redemption[] = []): Balance {
  const availableGross = credits
    .filter((credit) => credit.status === "available")
    .reduce((sum, credit) => sum + credit.amountCents, 0);
  const pendingCents = credits
    .filter((credit) => credit.status === "pending")
    .reduce((sum, credit) => sum + credit.amountCents, 0);
  const reservedCents = redemptions
    .filter((item) => item.status === "requested" || item.status === "approved")
    .reduce((sum, item) => sum + item.amountCents, 0);
  const paidCents = redemptions
    .filter((item) => item.status === "paid")
    .reduce((sum, item) => sum + item.amountCents, 0);
  return {
    availableCents: Math.max(availableGross - reservedCents - paidCents, 0),
    pendingCents,
    reservedCents,
  };
}

export function goalGap(goal: Goal | null, availableCents: number): number {
  return goal ? Math.max(goal.amountCents - availableCents, 0) : 0;
}

function duration(offer: MemberOffer): number {
  return offer.estimatedMinutes ?? Number.MAX_SAFE_INTEGER;
}

function stableCompare(a: MemberOffer, b: MemberOffer): number {
  return b.priority - a.priority || a.id.localeCompare(b.id);
}

export function eligibleOffers(offers: MemberOffer[]): MemberOffer[] {
  return offers.filter((offer) => offer.status === "active" && offer.memberState === "available");
}

export function recommendOffer(offers: MemberOffer[], gapCents: number): Recommendation | null {
  const eligible = eligibleOffers(offers);
  if (!eligible.length) return null;

  if (gapCents <= 0) {
    const [best] = [...eligible].sort(
      (a, b) => Number(b.quickWin) - Number(a.quickWin) || duration(a) - duration(b) || stableCompare(a, b),
    );
    return { offerId: best.id, reason: "general" };
  }

  const reaches = eligible.filter((offer) => offer.rewardCents >= gapCents);
  if (reaches.length) {
    const [best] = reaches.sort(
      (a, b) =>
        a.rewardCents - gapCents - (b.rewardCents - gapCents) ||
        Number(b.quickWin) - Number(a.quickWin) ||
        duration(a) - duration(b) ||
        stableCompare(a, b),
    );
    const reason = best.rewardCents === gapCents ? "exact_gap" : "smallest_overshoot";
    return { offerId: best.id, reason };
  }

  const [best] = eligible.sort(
    (a, b) =>
      b.rewardCents - a.rewardCents ||
      Number(b.quickWin) - Number(a.quickWin) ||
      duration(a) - duration(b) ||
      stableCompare(a, b),
  );
  return { offerId: best.id, reason: "largest_progress" };
}

export function memberLifecycle(credits: Credit[], distinctSessionDays = 1) {
  const distinctOffers = new Set(
    credits.filter((credit) => credit.status === "available" && credit.source !== "bonus" && credit.offerId).map((credit) => credit.offerId),
  ).size;
  if (distinctOffers >= 4) return "deep_earner" as const;
  if (distinctOffers >= 3 && distinctSessionDays >= 2) return "retained_multi_offer_earner" as const;
  if (distinctOffers >= 2) return "multi_offer_earner" as const;
  if (distinctOffers >= 1) return "activated" as const;
  return "new" as const;
}

export function isGoalReached(goal: Goal | null, availableCents: number): boolean {
  return Boolean(goal && (goal.status === "reached" || availableCents >= goal.amountCents));
}
